import type { Course, Item } from '../content/types';
import { looseText } from './greek';
import { shuffle } from './plan';

/**
 * Up to four meanings for a hear-it card: the phrase's own and three others from the situation,
 * all different when compared loosely. Phrases the learner has already met come first: a
 * meaning never seen before is easy to rule out, which turns the card into a guess. Then
 * look-alikes: phrases on the same pattern (έναν καφέ / ένα νερό / μια μπίρα), lines of the
 * same kind (waiter or you) and from the same lesson, so the learner has to listen to the
 * words rather than guess from the situation. While fewer than three met phrases share the
 * speaker, there are only three choices, so fillers that stand out are rarely needed.
 */
export function hearOptions(
  item: Item,
  course: Course,
  random: () => number = Math.random,
  met: (itemId: string) => boolean = () => true,
): string[] {
  const own = looseText(item.meaning);
  const seen = new Set([own]);
  const scored = course.items
    .filter((other) => other.id !== item.id)
    .map((other) => {
      let score = random() * 1.5;
      // Met phrases always come first; among them, the same speaker (a waiter's line among
      // waiters' lines), so neither novelty nor who speaks gives the answer away.
      if (met(other.id)) score += 12;
      if (other.role === item.role) score += 4;
      if (item.slot && other.slot?.frame === item.slot.frame) score += 3;
      // Same kind of line (a waiter's line among waiters' lines), so the situation gives nothing away.
      if (other.role === item.role) score += 2.5;
      if (other.lesson === item.lesson) score += 2;
      return { meaning: other.meaning, score, met: met(other.id), role: other.role };
    })
    .sort((a, b) => b.score - a.score);
  // Fewer choices rather than fillers: three while fewer than three met phrases share the
  // speaker, so a learner's line rarely stands out among the waiter's (or the reverse).
  const peers = new Set(scored.filter((o) => o.met && o.role === item.role).map((o) => looseText(o.meaning)));
  peers.delete(own);
  const wanted = peers.size < 3 ? 2 : 3;

  const picks: string[] = [];
  for (const { meaning } of scored) {
    const key = looseText(meaning);
    if (seen.has(key)) continue;
    seen.add(key);
    picks.push(meaning);
    if (picks.length === wanted) break;
  }
  return shuffle([item.meaning, ...picks], random);
}
